import arrayMove from 'array-move';
import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  CollisionDetection,
  DndContext,
  closestCenter,
  DragOverlay,
  defaultDropAnimationSideEffects,
  DropAnimation,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { DragHandleProvider } from '../../hooks/useDragHandle';

/**
 * Drop animation configuration.
 */
const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.4',
      },
    },
  }),
};

export const DROP_BEFORE = 'before';
export const DROP_AFTER = 'after';

/**
 * In drop-indicator mode items stay put while dragging; a line marks where the item will land.
 */
const noShiftStrategy = () => null;

const pointerWithinThenClosestCenter: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  return pointerCollisions.length ? pointerCollisions : closestCenter(args);
};

/**
 * Drag overlay to mirror the active (dragged) item.
 *
 * inPortal renders the preview into <body> (ancestor overflow/transform mis-position the
 * fixed DragOverlay); wrapper rebuilds the ancestor DOM its item styling is scoped to.
 */
const SortableOverlay = ({ children, inPortal = false, wrapper = undefined, dropAnimation = dropAnimationConfig }) => {
  const overlay = (
    <DragOverlay className={inPortal ? 'sortable-drag-overlay' : undefined} dropAnimation={dropAnimation}>
      {wrapper ? wrapper(children) : children}
    </DragOverlay>
  );
  return inPortal ? createPortal(overlay, document.body) : overlay;
};

/**
 * Custom keyboard sensor that respects certain element types and uses Enter or Space to activate.
 */
export const KeyboardSensorThatHandlesElementType = Object.assign(
  function KeyboardSensorThatHandlesElementType(props: ConstructorParameters<typeof KeyboardSensor>[0]) {
    return new KeyboardSensor(props);
  },
  { activators: [
    {
      eventName: 'onKeyDown',
      handler: ({ nativeEvent: event }: React.KeyboardEvent<Element>) => {
        if (!shouldHandleEvent(event)) {
          return false;
        }
        return event.key === 'Enter' || event.key === ' ';
      },
    },
  ] }
) as unknown as typeof KeyboardSensor;

/**
 * Custom pointer sensor that respects certain element types.
 */
export const PointerSensorThatHandlesElementType = Object.assign(
  function PointerSensorThatHandlesElementType(props: ConstructorParameters<typeof PointerSensor>[0]) {
    return new PointerSensor(props);
  },
  { activators: [
    {
      eventName: 'onPointerDown',
      handler: ({ nativeEvent: event }: React.PointerEvent<Element>) =>
        shouldHandleEvent(event),
    },
  ] }
) as unknown as typeof PointerSensor;

/**
 * Helper function to determine whether the event should be handled.
 */
function shouldHandleEvent(event: React.SyntheticEvent) {
  if ((event as React.KeyboardEvent).key === 'Tab') {
    return false;
  }
  const target = event.target;

  return !(target instanceof Element && target.closest('input, select, textarea, button, [contenteditable="true"]'));
}

/**
 * A helper component that wraps (clones) an existing element with sortable functionality.
 */
const SortableItem = ({
  helperClass = '',
  id,
  itemElement,
  order,
  useDragHandle = false,
  disabled = false,
  onDisabledInteraction = undefined,
  showDropIndicator = false,
  dropPosition = null,
}) => {
  const customAccessibleLabel = itemElement.props['data-sortable-label'];
  const accessibleLabel = customAccessibleLabel || `Row ${order}`;
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } =
    useSortable({ data: { accessibleLabel }, id });
  const indicatorClasses = showDropIndicator
    ? [isDragging && 'sortable-dragging', dropPosition && `sortable-drop-${dropPosition}`].filter(Boolean)
    : [];
  const itemClassName = [itemElement.props.className, helperClass, ...indicatorClasses].filter(Boolean).join(' ');

  const activeListeners = disabled
    ? {
      onPointerDown: (e: React.PointerEvent) => { e.preventDefault(); onDisabledInteraction?.(); },
      onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDisabledInteraction?.(); } },
    }
    : listeners;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const tabIndex =
    itemElement.props.tabIndex !== undefined ? itemElement.props.tabIndex : 0;

  if (useDragHandle) {
    // When using a drag handle, the row should be completely transparent to AT
    // Only the drag handle is interactive for reordering
    const dragHandleAttributes = {
      ...attributes,
      'aria-disabled': disabled || undefined,
      'aria-label': customAccessibleLabel ? `Reorder ${accessibleLabel}` : `Reorder row ${order}`,
      'aria-roledescription': 'sortable',
      role: 'button',
      tabIndex: 0,
    };

    // Row gets minimal attributes - no role, no tabIndex, no aria-label
    // Content inside should be read and interacted with normally
    const clonedElement = React.cloneElement(itemElement, {
      'data-id': id,
      className: itemClassName,
      ref: setNodeRef,
      style: { ...(itemElement.props.style || {}), ...style },
    });

    return (
      <DragHandleProvider
        attributes={dragHandleAttributes}
        listeners={activeListeners}
      >
        {clonedElement}
      </DragHandleProvider>
    );
  }

  // When not using a drag handle, the whole row is draggable and interactive
  const mergedAttributes = {
    ...attributes,
    'aria-disabled': disabled || undefined,
    'aria-label': `Row ${order}`,
    'aria-roledescription': 'sortable',
    role: attributes.role,
  };

  const clonedElement = React.cloneElement(itemElement, {
    'data-id': id,
    className: itemClassName,
    ref: setNodeRef,
    style: { ...(itemElement.props.style || {}), ...style },
    tabIndex,
    ...activeListeners,
    ...mergedAttributes,
  });

  return clonedElement;
};

/**
 * The main SortableList component.
 */
export const SortableList = ({
  containerElement = null,
  data,
  helperClass,
  renderOverlayItem = null,
  sortCallback,
  useDragHandle,
  disabled = false,
  onDisabledInteraction = undefined,
  overlayInPortal = false,
  overlayWrapper = undefined,
  showDropIndicator = false,
  accessibilityContainer = undefined,
}) => {
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [dropTarget, setDropTarget] = React.useState<{ id: string, position: string } | null>(null);
  const pointerY = React.useRef<number | null>(null);

  const pointerSensor = useSensor(PointerSensorThatHandlesElementType, {
    activationConstraint: { distance: 8 },
  });
  const keyboardSensor = useSensor(KeyboardSensorThatHandlesElementType, {
    coordinateGetter: sortableKeyboardCoordinates,
  });
  const sensors = useSensors(pointerSensor, keyboardSensor);

  React.useEffect(() => {
    if (!showDropIndicator || activeId === null) { return }

    const trackPointer = (e: PointerEvent) => { pointerY.current = e.clientY };
    window.addEventListener('pointermove', trackPointer, true);
    return () => {
      window.removeEventListener('pointermove', trackPointer, true);
      pointerY.current = null;
    };
  }, [showDropIndicator, activeId]);

  const indexOfKey = (key) => data.findIndex((item) => String(item.key) === String(key));

  const focusMovedItem = (id) => {
    setTimeout(() => {
      const movedElement = document.querySelector<HTMLElement>(
        `[data-id=\"${id}\"]`
      );
      const dragHandle = movedElement?.querySelector<HTMLElement>(
        '[aria-roledescription="sortable"]'
      );
      (dragHandle || movedElement)?.focus();
    });
  };

  const currentPointerY = ({ activatorEvent, delta }) => {
    if (pointerY.current !== null) { return pointerY.current }

    return typeof activatorEvent?.clientY === 'number' ? activatorEvent.clientY + delta.y : null;
  };

  const dropPositionOver = (event) => {
    const { active, over } = event;
    const overElement = document.querySelector<HTMLElement>(`[data-id=\"${over.id}\"]`);
    const y = currentPointerY(event);
    if (y !== null && overElement) {
      const { top, height } = overElement.getBoundingClientRect();
      return y < top + height / 2 ? DROP_BEFORE : DROP_AFTER;
    }
    return indexOfKey(active.id) < indexOfKey(over.id) ? DROP_AFTER : DROP_BEFORE;
  };

  const dropTargetFor = (event) => {
    const { active, over } = event;
    if (!over || String(over.id) === String(active.id)) { return null }

    return { id: String(over.id), position: dropPositionOver(event) };
  };

  const updateDropTarget = (event) => {
    if (!showDropIndicator) { return }

    const next = dropTargetFor(event);
    setDropTarget((current) => (
      current?.id === next?.id && current?.position === next?.position ? current : next
    ));
  };

  const handleIndicatorDrop = (event) => {
    const { active } = event;
    const target = dropTargetFor(event);
    if (!target) { return }

    const movedItem = data[indexOfKey(active.id)];
    const rest = data.filter((item) => String(item.key) !== String(active.id));
    const targetIndex = rest.findIndex((item) => String(item.key) === target.id);
    const insertAt = target.position === DROP_BEFORE ? targetIndex : targetIndex + 1;
    const newData = [...rest.slice(0, insertAt), movedItem, ...rest.slice(insertAt)];
    sortCallback(newData, { movedId: active.id, targetId: target.id, position: target.position });
    focusMovedItem(active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (showDropIndicator) {
      handleIndicatorDrop(event);
    } else if (active && over && active.id !== over.id) {
      const newData = arrayMove(data, indexOfKey(active.id), indexOfKey(over.id));
      sortCallback(newData);
      focusMovedItem(active.id);
    }
    setActiveId(null);
    setDropTarget(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setDropTarget(null);
  };

  const handleDragStart = ({ active }) => {
    setActiveId(active.id);
  };

  const sortableItems = data.map((item, i) => (
    <SortableItem
      disabled={disabled}
      dropPosition={dropTarget?.id === String(item.key) ? dropTarget.position : null}
      helperClass={helperClass}
      id={item.key}
      itemElement={item}
      key={String(item.key)}
      onDisabledInteraction={onDisabledInteraction}
      order={i + 1}
      showDropIndicator={showDropIndicator}
      useDragHandle={useDragHandle}
    />
  ));

  const renderActiveItem = () => {
    const index = data.findIndex((item) => String(item.key) === String(activeId));
    const itemElement = data[index];
    if (!itemElement) { return null }
    if (renderOverlayItem) { return renderOverlayItem(itemElement) }

    return sortableItems[index];
  };

  return (
    <DndContext
      accessibility={{
        announcements: {
          onDragCancel: ({ active }) =>
            `Drag cancelled for ${active.data.current?.accessibleLabel}`,
          onDragEnd: ({ active, over }) =>
            over
              ? `Dropped ${active.data.current?.accessibleLabel} over ${over.data.current?.accessibleLabel || over.id}`
              : `Dropped ${active.data.current?.accessibleLabel}`,
          onDragOver: ({ active }) =>
            `${active.data.current?.accessibleLabel} currently being dragged`,
          onDragStart: ({ active }) =>
            `Picked up ${active.data.current?.accessibleLabel}`,
        },
        container: accessibilityContainer,
      }}
      collisionDetection={showDropIndicator ? pointerWithinThenClosestCenter : closestCenter}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
      onDragMove={updateDropTarget}
      onDragOver={updateDropTarget}
      onDragStart={handleDragStart}
      sensors={sensors}
    >
      <SortableContext
        items={data.map((item) => item.key)}
        strategy={showDropIndicator ? noShiftStrategy : verticalListSortingStrategy}
      >
        {containerElement
          ? React.cloneElement(containerElement, undefined, sortableItems)
          : sortableItems}
      </SortableContext>
      <SortableOverlay
        dropAnimation={showDropIndicator ? null : dropAnimationConfig}
        inPortal={overlayInPortal}
        wrapper={overlayWrapper}
      >
        {activeId ? renderActiveItem() : null}
      </SortableOverlay>
    </DndContext>
  );
};
